import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import { Mermaid } from './Mermaid';
import { RecentChanges } from './RecentChanges';
import type { MarkdownPageProps, NavItem } from '../types';

function findFileForPath(items: NavItem[], targetPath: string): string | null {
  for (const item of items) {
    if (item.path === targetPath && item.file) {
      return item.file;
    }
    if (item.children.length > 0) {
      const found = findFileForPath(item.children, targetPath);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Check if a path represents a "directory" (renders a README.md file)
 * vs a regular document file.
 *
 * Directory paths like /guides render guides/README.md
 * File paths like /guides/aws-cli-setup render guides/aws-cli-setup.md
 */
function isDirectory(items: NavItem[], targetPath: string): boolean {
  const file = findFileForPath(items, targetPath);
  if (!file) return false;
  return file.endsWith('README.md');
}

function transformLink(
  href: string,
  currentPath: string,
  items: NavItem[]
): string {
  if (!href) return href;

  // External links - keep as-is
  if (href.startsWith('http://') || href.startsWith('https://')) {
    return href;
  }

  // Anchor links - keep as-is
  if (href.startsWith('#')) {
    return href;
  }

  // Resolve relative paths for both .md links and static files (PDFs, images, etc.)
  const isRelative =
    href.startsWith('./') || href.startsWith('../') || !href.startsWith('/');
  const isMarkdown = href.endsWith('.md');

  if (isMarkdown || isRelative) {
    let newHref = href;

    if (isMarkdown) {
      // Remove .md extension
      newHref = newHref.replace(/\.md$/, '');
      // Handle README.md -> directory index
      newHref = newHref.replace(/\/README$/, '').replace(/^README$/, '');
    }

    // Handle relative paths
    if (newHref.startsWith('./')) {
      newHref = newHref.substring(2);
    }

    // Resolve ../ paths based on current location.
    // Also handles ".." alone (e.g. from "../README.md" after stripping /README).
    if (newHref === '..' || newHref.startsWith('../')) {
      const currentParts = currentPath.split('/').filter(Boolean);
      const hrefParts = newHref.split('/');

      // If current path is a file (not a directory index), strip the filename
      // before resolving ../. e.g. /s7-info-ops/business-development is a file
      // in /s7-info-ops/, so ../ should go up to /, not stay at /s7-info-ops/.
      if (!isDirectory(items, currentPath)) {
        currentParts.pop();
      }

      while (hrefParts[0] === '..') {
        hrefParts.shift();
        currentParts.pop();
      }

      // Result is an absolute path from root, add leading slash
      newHref = '/' + [...currentParts, ...hrefParts].join('/');
    }

    // Ensure leading slash and resolve relative to current path
    if (newHref && !newHref.startsWith('/')) {
      // Determine if currentPath represents a "directory" (renders README.md) or a "file"
      // Directory paths: /, /guides, /rostersmith, etc. -> links are relative to this directory
      // File paths: /guides/aws-cli-setup, etc. -> links are relative to parent directory
      const isDirectoryPath = isDirectory(items, currentPath);

      if (isDirectoryPath) {
        // Current path is a directory, link is relative to it
        // /rostersmith + overview/file = /rostersmith/overview/file
        newHref =
          currentPath === '/' ? `/${newHref}` : `${currentPath}/${newHref}`;
      } else {
        // Current path is a file, link is relative to parent directory
        // /guides/some-doc + other-doc = /guides/other-doc
        const lastSlashIndex = currentPath.lastIndexOf('/');
        const parentDir =
          lastSlashIndex > 0 ? currentPath.substring(0, lastSlashIndex) : '';
        newHref = `${parentDir}/${newHref}`;
      }

      if (!newHref.startsWith('/')) {
        newHref = '/' + newHref;
      }
    }

    // Static files need /docs prefix so the server can find them
    if (!isMarkdown) {
      return `/docs${newHref}`;
    }

    return newHref || '/';
  }

  return href;
}

export function MarkdownPage({
  navigation,
  showRecentChanges = false,
}: MarkdownPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadContent() {
      setLoading(true);
      setError(null);

      const currentPath = location.pathname === '/' ? '/' : location.pathname;
      const file = findFileForPath(navigation.items, currentPath);

      if (!file) {
        setError('Page not found');
        setLoading(false);
        return;
      }

      try {
        // Fetch markdown file from docs directory
        const response = await fetch(`/docs/${file}`);
        if (!response.ok) {
          throw new Error(`Failed to load: ${response.statusText}`);
        }
        const text = await response.text();

        // Remove frontmatter if present
        const contentWithoutFrontmatter = text.replace(
          /^---[\s\S]*?---\n*/,
          ''
        );
        setContent(contentWithoutFrontmatter);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setLoading(false);
      }
    }

    loadContent();
  }, [location.pathname, navigation.items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-docs-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-950">
        <h1 className="text-xl font-semibold text-red-800 dark:text-red-200">
          {error === 'Page not found' ? 'Page Not Found' : 'Error Loading Page'}
        </h1>
        <p className="mt-2 text-red-600 dark:text-red-400">
          {error === 'Page not found'
            ? `The page "${location.pathname}" could not be found.`
            : error}
        </p>
      </div>
    );
  }

  const isHomepage = location.pathname === '/';
  const recentChanges = navigation.recentChanges || [];

  return (
    <>
      {/* Show recent changes on homepage if enabled */}
      {isHomepage && showRecentChanges && (
        <RecentChanges changes={recentChanges} />
      )}

      <article className="prose prose-gray dark:prose-invert max-w-none prose-headings:scroll-mt-20 prose-headings:text-[var(--color-site-header)] dark:prose-headings:text-[var(--color-site-header-light)] prose-a:text-docs-600 prose-a:no-underline dark:prose-a:text-docs-400">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeRaw, rehypeSlug]}
          components={{
            a: ({ href, children, ...props }) => {
              const transformedHref = transformLink(
                href || '',
                location.pathname,
                navigation.items
              );
              const isExternal =
                transformedHref.startsWith('http://') ||
                transformedHref.startsWith('https://');

              if (isExternal) {
                return (
                  <a
                    href={transformedHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    {...props}
                  >
                    {children}
                  </a>
                );
              }

              // Handle anchor links for same-page navigation
              if (transformedHref.startsWith('#')) {
                return (
                  <a
                    href={transformedHref}
                    className="hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      const targetId = transformedHref.slice(1);
                      const element = document.getElementById(targetId);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    {...props}
                  >
                    {children}
                  </a>
                );
              }

              // Static files (PDFs, images, etc.) - let browser handle directly
              const isStaticFile = transformedHref.startsWith('/docs/');
              if (isStaticFile) {
                return (
                  <a
                    href={transformedHref}
                    className="hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                );
              }

              return (
                <a
                  href={transformedHref}
                  className="hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(transformedHref);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            },
            img: ({ src, alt, ...props }) => {
              const transformedSrc = transformLink(
                src || '',
                location.pathname,
                navigation.items
              );
              return (
                <img
                  src={transformedSrc}
                  alt={alt || ''}
                  style={{ maxHeight: '180px', width: 'auto' }}
                  {...props}
                />
              );
            },
            code: ({ className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || '');
              const language = match ? match[1] : '';
              const codeString = String(children).replace(/\n$/, '');

              if (language === 'mermaid') {
                return <Mermaid chart={codeString} />;
              }

              // Inline code
              if (!className) {
                return (
                  <code
                    className="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800"
                    {...props}
                  >
                    {children}
                  </code>
                );
              }

              // Code block
              return (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </article>
    </>
  );
}
